/**
 * Online state (FE-04 FR-FE04-28): `navigator.onLine` + `online`/`offline` events + the api-client:
 * a request that fails without an answer (`NetworkError`, offline) marks the app offline until the
 * next answered request or the next `online` event. Timeouts don't count (a slow server is not
 * "no signal").
 */
import { useSyncExternalStore } from "react";

let apiOffline = false;
const listeners = new Set<() => void>();
let attached = false;

function emit(): void {
  for (const listener of listeners) listener();
}

function attach(): void {
  if (attached || typeof window === "undefined") return;
  attached = true;
  window.addEventListener("online", () => {
    apiOffline = false;
    emit();
  });
  window.addEventListener("offline", emit);
}

/** Called by the api-client for every request outcome (shared/api/client.ts). */
export function reportNetworkStatus(status: "online" | "offline"): void {
  const next = status === "offline";
  if (next === apiOffline) return;
  apiOffline = next;
  emit();
}

export function isOnline(): boolean {
  const browser = typeof navigator === "undefined" ? true : navigator.onLine;
  return browser && !apiOffline;
}

function subscribe(listener: () => void): () => void {
  attach();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useOnlineStatus(): boolean {
  return useSyncExternalStore(subscribe, isOnline, () => true);
}

/** Test helper. */
export function resetOnlineStatusForTests(): void {
  apiOffline = false;
  emit();
}
